import { ReactNode } from "react";
import { PlusIcon } from "@heroicons/react/24/outline";
import { ClassesIcon } from "./navIcons";
import { slotDotStyle, type Cover } from "@ui/tokens/classCover";
import { BrandMark } from "../business/BrandMark";
import { Skeleton, SkeletonRegion } from "../primitives/Skeleton";
import { focusRing, focusRingOnInk } from "@ui/tokens/interaction";

export interface NavEntry {
  id: string;
  label: string;
  icon: ReactNode;
  /** Drawn but inert — the destination isn't ready yet. */
  disabled?: boolean;
}

export interface NavClass {
  id: string;
  name: string;
  cover: Cover;
}

interface NavMenuProps {
  primary: readonly NavEntry[];
  secondary: readonly NavEntry[];
  /** Pinned to the bottom of the column — settings are a destination, not part of the flow. */
  footer: readonly NavEntry[];
  classes: readonly NavClass[];
  loadingClasses?: boolean;
  activeId: string | null;
  activeClassId: string | null;
  onSelect: (id: string) => void;
  onSelectClass: (id: string) => void;
  onAddClass?: () => void;
}

const entryBase =
  "group relative flex items-center gap-3.5 pl-3 pr-5 py-1.5 rounded-pill text-left text-control-lg transition duration-hover";

const activeEntry =
  "font-700 bg-transparent bg-gradient-to-r from-nav-wash/20 to-nav-wash/5 text-ink shadow-nav-entry after:content-[''] after:absolute after:[display:var(--nav-active-dot-display)] max-desk:after:hidden after:right-5 after:w-2 after:h-2 after:rounded-full after:bg-nav-icon";

/**
 * A section of the app and a piece of content are marked differently, on purpose.
 * A nav entry gets the 5px signature rule — "you are here in the application", one of the four
 * sanctioned pink usages. A class entry does not: its identity already comes from its own
 * colour, and adding pink would blur it. Both share the ink pill with light-toned text.
 */
const NavLink = ({
  entry,
  active,
  onSelect,
}: {
  entry: NavEntry;
  active: boolean;
  onSelect: () => void;
}) => (
  <button
    type="button"
    onClick={onSelect}
    disabled={entry.disabled}
    aria-current={active ? "page" : undefined}
    className={`${entryBase} ${
      entry.disabled
        ? "text-ink-meta-faint cursor-default"
        : active
          ? `${activeEntry} ${focusRingOnInk}`
          : `text-ink-body font-600 hover:bg-row-hover ${focusRing}`
    }`}
  >
    <span
      className={`flex items-center justify-center shrink-0 w-10 h-10 rounded-pill transition duration-hover ${
        active ? "bg-nav-icon text-nav-icon-ink shadow-nav-entry-icon" : "group-hover:bg-tint/10"
      }`}
    >
      {entry.icon}
    </span>
    {entry.label}
  </button>
);

export const NavMenu = ({
  primary,
  secondary,
  footer,
  classes,
  loadingClasses = false,
  activeId,
  activeClassId,
  onSelect,
  onSelectClass,
  onAddClass,
}: NavMenuProps) => (
  <div className="flex flex-col min-h-0 h-full">
    {/* Same height as the top bar, so the brand sits on its baseline. */}
    <div className="flex items-center shrink-0 h-topbar px-4 desk:px-7">
      <BrandMark className="pl-3.75" />
    </div>

    <nav className="flex flex-col flex-1 gap-1 min-h-0 overflow-y-auto scrollbar-none px-4 desk:px-7 pb-8">
      {primary.map((entry) => (
        <NavLink
          key={entry.id}
          entry={entry}
          active={activeId === entry.id}
          onSelect={() => onSelect(entry.id)}
        />
      ))}

      {secondary.map((entry) => (
        <NavLink
          key={entry.id}
          entry={entry}
          active={activeId === entry.id}
          onSelect={() => onSelect(entry.id)}
        />
      ))}

      <div className="flex items-center justify-between gap-2 pt-10 pb-3 px-5">
      <span className="inline-flex items-center gap-3.25 whitespace-nowrap text-section-label uppercase text-ink-meta">
        <ClassesIcon />
        Mes classes
      </span>
      {onAddClass && (
        <button
          type="button"
          onClick={onAddClass}
          aria-label="Nouvelle classe"
          className={`relative inline-flex items-center justify-center shrink-0 w-9 h-9 rounded-full text-ink bg-tint/10 hover:bg-tint/20 transition duration-hover after:absolute after:left-1/2 after:top-1/2 after:w-touch after:h-touch after:-translate-x-1/2 after:-translate-y-1/2 after:content-[''] ${focusRing}`}
        >
          <PlusIcon className="w-4.5 h-4.5" strokeWidth={2.2} aria-hidden="true" />
        </button>
      )}
    </div>

    <div className="flex flex-col gap-1 pl-0 pb-3">
      {loadingClasses && (
        <SkeletonRegion visible className="flex flex-col gap-1">
          {[0, 1, 2].map((index) => (
            <div key={index} className="flex h-[37px] items-center gap-4 px-5">
              <Skeleton className="shrink-0 w-2.5 h-2.5 rounded-full" />
              <Skeleton className="h-3.5 w-28 rounded-sm" />
            </div>
          ))}
        </SkeletonRegion>
      )}
      {classes.map(({ id, name, cover }) => {
        const active = activeClassId === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onSelectClass(id)}
            aria-current={active ? "page" : undefined}
            style={active ? { color: cover.c } : undefined}
            className={`flex items-center gap-4 px-5 py-2.5 rounded-pill text-left text-control font-600 transition duration-hover ${
              active ? `bg-signature/[0.12] !text-ink shadow-nav-class ${focusRingOnInk}` : `text-ink-body hover:bg-row-hover ${focusRing}`
            }`}
          >
            <span
              aria-hidden="true"
              style={slotDotStyle(cover)}
              className="block shrink-0 w-2.5 h-2.5 rounded-full"
            />
            {name}
          </button>
        );
      })}
    </div>

      <div className="flex flex-col gap-0.5 mt-auto pt-3">
        {footer.map((entry) => (
          <NavLink
            key={entry.id}
            entry={entry}
            active={activeId === entry.id}
            onSelect={() => onSelect(entry.id)}
          />
        ))}
      </div>
    </nav>
  </div>
);
